import { findResult, type SearchEntry, type SearchResult } from "./flight-search";
import type { Origin, Workspace } from "./types";
import { cityKey, originKey, routeKey } from "./workspace";

/** One traveler group's flight to the city, as the detailed flight costs rows show it. */
export interface GroupFlight {
  origin: Origin;
  key: string;
  cabin: string;
  result: SearchResult | null;
  /** Fare per traveler, USD: the planner's own fare when set, else the live one; null when none. */
  costPerTraveler: number | null;
  overridden: boolean;
  total: number | null;
}

/** All groups flying from one airport (or one home city before airports resolve). */
export interface DepartureFlights {
  code: string;
  name: string;
  travelers: number;
  total: number;
  avgCost: number | null;
  avgMinutes: number | null;
  layovers: number;
  groups: GroupFlight[];
}

/** A destination's detailed flight costs: per departure, plus the totals across everyone. */
export interface CityFlights {
  departures: DepartureFlights[];
  travelers: number;
  pricedTravelers: number;
  total: number;
  avgCost: number | null;
  avgMinutes: number | null;
  layovers: number;
  carbonKg: number;
}

/**
 * Groups the search's results for one destination by departure airport, with totals at the
 * current headcounts (a headcount edit doesn't need a new search) and the planner's fare
 * overrides applied. A group with no fare adds travelers but no cost, never a $0 fare.
 * @param workspace - The workspace.
 * @param entry - The search for the current plan.
 * @param destination - Destination city.
 * @returns The city's flight breakdown.
 * @throws Never.
 */
export const buildCityFlights = (workspace: Workspace, entry: SearchEntry | null, destination: string): CityFlights => {
  const byDeparture = new Map<string, DepartureFlights>();
  let carbonKg = 0;

  for (const origin of workspace.origins) {
    if (cityKey(origin.city) === cityKey(destination)) continue;
    const key = originKey(origin);
    const result = entry ? findResult(entry, origin, destination, workspace) : null;
    const override = workspace.fareOverrides[routeKey(key, destination)];
    const live = result?.flight && result.flight.price > 0 ? result.flight.price : null;
    const costPerTraveler = override ?? live;
    const code = result?.departureCode ?? entry?.started?.airports[cityKey(origin.city)] ?? origin.city;
    const departure = byDeparture.get(code) ?? {
      code,
      name: entry?.poll?.airportNames[code] ?? origin.city,
      travelers: 0,
      total: 0,
      avgCost: null,
      avgMinutes: null,
      layovers: 0,
      groups: [],
    };
    departure.groups.push({
      origin,
      key,
      cabin: origin.cabin ?? workspace.cabin,
      result,
      costPerTraveler,
      overridden: override !== undefined,
      total: costPerTraveler === null ? null : costPerTraveler * origin.count,
    });
    departure.travelers += origin.count;
    departure.layovers += (result?.flight?.layovers ?? 0) * origin.count;
    carbonKg += (result?.flight?.carbonKg ?? 0) * origin.count;
    byDeparture.set(code, departure);
  }

  const departures = [...byDeparture.values()].map((departure) => {
    const priced = departure.groups.filter((group) => group.costPerTraveler !== null);
    const pricedTravelers = priced.reduce((sum, group) => sum + group.origin.count, 0);
    const total = priced.reduce((sum, group) => sum + (group.total ?? 0), 0);
    const timed = departure.groups.filter((group) => group.result?.flight?.minutes);
    const timedTravelers = timed.reduce((sum, group) => sum + group.origin.count, 0);
    const minutes = timed.reduce((sum, group) => sum + (group.result?.flight?.minutes ?? 0) * group.origin.count, 0);
    return {
      ...departure,
      total,
      avgCost: pricedTravelers ? total / pricedTravelers : null,
      avgMinutes: timedTravelers ? minutes / timedTravelers : null,
    };
  });

  const travelers = departures.reduce((sum, departure) => sum + departure.travelers, 0);
  const allGroups = departures.flatMap((departure) => departure.groups);
  const pricedTravelers = allGroups
    .filter((group) => group.costPerTraveler !== null)
    .reduce((sum, group) => sum + group.origin.count, 0);
  const total = departures.reduce((sum, departure) => sum + departure.total, 0);
  const timed = allGroups.filter((group) => group.result?.flight?.minutes);
  const timedTravelers = timed.reduce((sum, group) => sum + group.origin.count, 0);
  const minutes = timed.reduce((sum, group) => sum + (group.result?.flight?.minutes ?? 0) * group.origin.count, 0);

  return {
    departures,
    travelers,
    pricedTravelers,
    total,
    avgCost: pricedTravelers ? total / pricedTravelers : null,
    avgMinutes: timedTravelers ? minutes / timedTravelers : null,
    layovers: departures.reduce((sum, departure) => sum + departure.layovers, 0),
    carbonKg,
  };
};
